import React, { useState, useRef, useEffect } from 'react';
import { EnvelopeIcon, ArrowPathIcon, CheckCircleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

/**
 * Reusable Email Verification Component
 * Shows a 6-digit OTP code input for email verification during registration
 * 
 * Props:
 *  - email: The email being verified
 *  - onVerify: (code) => Promise — called when user submits the code
 *  - onResend: () => Promise — called when user clicks resend
 *  - onBack: () => void — called when user wants to go back to the form
 *  - loading: boolean — external loading state
 *  - error: string | null — external error message
 */
const EmailVerification = ({ email, onVerify, onResend, onBack, loading = false, error: externalError = null }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [localError, setLocalError] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const inputRefs = useRef([]);

  // Start cooldown timer on mount
  useEffect(() => {
    setResendCooldown(30);
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Clear local error when external error changes
  useEffect(() => {
    if (externalError) {
      setLocalError('');
    }
  }, [externalError]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setLocalError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleSubmit(fullCode);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace: clear current and move to previous
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      const newCode = [...code];
      newCode[index - 1] = '';
      setCode(newCode);
    }
    // Left arrow
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Right arrow
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newCode[i] = pastedData[i];
      }
      setCode(newCode);
      
      // Focus the next empty input or the last one
      const nextEmpty = newCode.findIndex(d => !d);
      const focusIndex = nextEmpty === -1 ? 5 : nextEmpty;
      inputRefs.current[focusIndex]?.focus();

      // Auto-submit if all 6 digits pasted
      if (pastedData.length === 6) {
        handleSubmit(pastedData);
      }
    }
  };

  const handleSubmit = async (fullCode) => {
    const codeStr = fullCode || code.join('');
    if (codeStr.length !== 6) {
      setLocalError('Please enter the complete 6-digit code');
      return;
    }
    try {
      await onVerify(codeStr);
    } catch (err) {
      // Error handling is managed by parent via externalError prop
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    setResendSuccess(false);
    try {
      await onResend();
      setResendCooldown(60);
      setResendSuccess(true);
      setCode(['', '', '', '', '', '']);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err) {
      setLocalError('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const displayError = externalError || localError;

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mx-auto w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mb-4">
          <ShieldCheckIcon className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Verify Your Email
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          We've sent a 6-digit verification code to
        </p>
        <p className="text-indigo-600 dark:text-indigo-400 font-semibold mt-1">
          {email}
        </p>
      </div>

      {/* Code Input */}
      <div className="flex justify-center gap-2 sm:gap-3 mb-6">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            className={`w-11 h-14 sm:w-13 sm:h-16 text-center text-xl sm:text-2xl font-bold rounded-xl border-2 
              transition-all duration-200 outline-none
              ${digit 
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300' 
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
              }
              focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800
              ${displayError ? 'border-red-400 dark:border-red-500' : ''}
            `}
            disabled={loading}
          />
        ))}
      </div>

      {/* Error Message */}
      {displayError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-center">
          <p className="text-sm text-red-600 dark:text-red-400">{displayError}</p>
        </div>
      )}

      {/* Resend Success */}
      {resendSuccess && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-center flex items-center justify-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
          <p className="text-sm text-green-600 dark:text-green-400">New verification code sent!</p>
        </div>
      )}

      {/* Verify Button */}
      <button
        onClick={() => handleSubmit()}
        disabled={loading || code.join('').length !== 6}
        className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 
          text-white font-semibold rounded-xl transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
          dark:focus:ring-offset-gray-900 disabled:cursor-not-allowed"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <ArrowPathIcon className="w-5 h-5 animate-spin" />
            Verifying...
          </span>
        ) : (
          'Verify & Create Account'
        )}
      </button>

      {/* Resend & Back */}
      <div className="mt-6 text-center space-y-3">
        <div className="flex items-center justify-center gap-1 text-sm text-gray-600 dark:text-gray-400">
          <EnvelopeIcon className="w-4 h-4" />
          <span>Didn't receive the code?</span>
          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || resending}
            className="text-indigo-600 dark:text-indigo-400 font-semibold hover:text-indigo-800 
              dark:hover:text-indigo-300 disabled:text-gray-400 dark:disabled:text-gray-600 
              disabled:cursor-not-allowed transition-colors"
          >
            {resending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
          </button>
        </div>
        
        {onBack && (
          <button
            onClick={onBack}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 
              dark:hover:text-gray-300 transition-colors"
          >
            ← Back to registration
          </button>
        )}

        <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
          Code expires in 10 minutes • Check your spam folder if not found
        </p>
      </div>
    </div>
  );
};

export default EmailVerification;
